<template>
  <DxDataGrid id="search-grid"
              class="la-datagrid"
              :data-source="searchGridDataStore"
              v-model:filter-value="headerFilter"
              :filter-sync-enabled="true"
              :remote-operations="true"
              :allow-column-resizing="true"
              column-resizing-mode="widget"
              :show-column-lines="true"
              :allow-column-reordering="true"
              :show-borders="true"
              :column-auto-width="false"
              :word-wrap-enabled="true"
              :show-row-lines="true"
              :grouping="{ contextMenuEnabled: false }"
              @initialized="initializeGrid"
              @row-updated="handleRowUpdated"
              @context-menu-preparing="addContextMenuItems"
              @editor-preparing="onEditorPreparing"
  >

    <DxStateStoring :enabled="true"
                    type="custom"
                    :custom-save="saveState"
                    :custom-load="loadState"
    />
    <DxColumnChooser title="Visible Columns"
                     :enabled="true"
                     mode="select"
                     :allow-search="true"
                     :width="480"
                     :height="520"
                     :position="columnChooserPosition"
                     :selection="{ allowSelectAll: true, selectByClick: true }"
    />
    <DxColumnFixing :enabled="true"/>
    <DxHeaderFilter :visible="true" :allow-search="true" :show-clear-button="true"/>
    <DxFilterRow :visible="true"/>
    <DxScrolling mode="virtual" :scroll-by-content="true" :use-native="true"/>
    <DxPaging :page-size="100"/>
    <DxEditing :allow-updating="editingEnabled" mode="cell"/>


    <DxToolbar>
      <DxItem :options="toolbarItemOptions.selectionsOnly" widget="dxButton" location="before" locate-in-menu="auto"/>
      <DxItem :options="toolbarItemOptions.resetSearch" widget="dxButton" location="before" locate-in-menu="auto"/>
      <DxItem :options="toolbarItemOptions.columnChooser" name="columnChooserButton" show-text="always"  location="before" locate-in-menu="auto"/>
      <DxItem :options="toolbarItemOptions.editToggle" widget="dxButton"  location="before"  locate-in-menu="auto"/>
      <DxItem template="total-count" location="after"/>
    </DxToolbar>

    <DxColumn v-for                   = "column in userColumnConfig"
              :header-filter          = "column.allowFiltering !== false ? { dataSource: createHeaderFilterStore(column.dataField) } : undefined"
              :data-field             = "column.dataField"
              :data-type              = "column.dataType"
              :caption                = "column.caption"
              :visible                = "column.visible"
              :show-in-column-chooser = "column.showInColumnChooser"
              :header-cell-template   = "column.headerCellTemplate"
              :cell-template          = "column.cellTemplate ?? (column.dataType === 'boolean' ? null : 'custom-display-cell')"
              :edit-cell-template     = "column.dataType === 'lookup' ? 'custom-edit-cell' : undefined"
              :allow-editing          = "column.allowEditing"
              :allow-sorting          = "column.allowSorting"
              :allow-filtering        = "column.allowFiltering"
              :allow-fixing           = "column.allowFixing"
              :allow-reordering       = "column.allowReordering"
              :allow-resizing         = "column.allowResizing"
              :group-interval         = "column.groupInterval"
              :width                  = "column.width ?? 200"
              :min-width              = "column.minWidth ?? 120"
              alignment               = "left"
              :css-class              = "column.allowEditing !== false && editingEnabled ? 'editable-cell' : null"
    />

    <template #main-doc-cell="{ data }">
      <MainDocCell :doc-id="data.data.doc_id"
                   :doc-title="data.data.doc_title"
                   :thumb-url="data.data.doc_thumb_url"
                   :tags="data.data.doc_tags"
                   :on-fetch-headlines="fetchHeadlines"
                   :text-search-strings="textSearchStrings"
                    @thumbnail-click="viewSingleDocument(data.data.doc_id)"
      />
    </template>

    <template #select-header-template>
      <SelectAllCheckbox/>
    </template>

    <template #select-template="{ data: cellInfo }">
      <SelectSingleCheckbox :selected="cellInfo.data.selected"
                            :doc-id="cellInfo.data.doc_id"
                            :row-data="cellInfo.data"
      />
    </template>

    <template #custom-display-cell="{ data }">
      <CustomDisplayCell v-model:value="data.value"
                :data="data"
                :update-cell="data.setValue"
      />
    </template>

    <template #custom-edit-cell="{ data }">
      <LookupDropdown :value="data.value"
                      :column-name="data.column.dataField"
                      @update:value="data.setValue($event)"
      />
    </template>

    <template #total-count>
      <div class="total-count-container">
        <span class="total-count"> {{ searchResultCount }} </span>
        <span> Documents </span>
      </div>
    </template>
  </DxDataGrid>
</template>


<script setup>
  import { ref, computed, watch } from 'vue'
  import { storeToRefs } from "pinia";
  import { addContextMenuItems } from "@/js-modules/searchGridContextMenu.js";
  import { DxDataGrid, DxColumn, DxStateStoring, DxEditing, DxColumnFixing, DxScrolling, DxPaging, DxHeaderFilter, DxFilterRow, DxColumnChooser, DxToolbar, DxItem } from "devextreme-vue/data-grid";
  import MainDocCell from "@/components/search-page/search-grid/custom-cells/MainDocCell.vue";
  import SelectSingleCheckbox from "@/components/search-page/search-grid/SelectSingleCheckbox.vue";
  import SelectAllCheckbox from "@/components/search-page/search-grid/SelectAllCheckbox.vue";
  import CustomDisplayCell from "@/components/search-page/search-grid/custom-cells/CustomDisplayCell.vue";
  import LookupDropdown from "@/components/base-components/LookupDropdown.vue";
  import { useSearchPageStore } from "@/stores/searchPageStore.js";
  import { useCabinetStore } from "@/stores/cabinetStore.js";

  // PINIA
  const searchPageStore = useSearchPageStore()
  const { searchGridInstance, selectedOnly, headerFilter, searchResultCount, docQueryParams, searchBarObjects, lastParams } = storeToRefs(searchPageStore)
  const { searchGridDataStore, createHeaderFilterStore, resetSearchAndFilter, refreshData, refreshSearchResults, fetchHeadlines } = searchPageStore

  const cabinetStore = useCabinetStore()
  const { cabinetId, userColumnConfig } = storeToRefs(cabinetStore)
  const { saveUserColumnConfig, getUserColumnConfig } = cabinetStore

  // Centered modal-style column chooser (defaults are 250×260 and bottom-right of the grid).
  const columnChooserPosition = {
    my: "center",
    at: "center",
    of: window,
    collision: "fit",
  }

  // TEMPLATE DATA
  const toolbarItemOptions = computed(() => {
    return {
      selectionsOnly: {
        text: 'View Selections',
        hint: 'Show Only Selected Documents',
        icon: 'checkmarkcircle',
        type: selectedOnly.value ? 'default' : null,
        stylingMode: selectedOnly.value ? 'contained' : null,
        onClick: () => selectedOnly.value = !selectedOnly.value
      },
      resetSearch: {
        text: 'Reset Search',
        hint: 'Reset search, filters, and column sort',
        icon: 'clearformat',
        onClick: resetSearchAndFilter
      },
      columnChooser: {
        text: 'Choose Columns',
        hint: 'Choose Visible Columns'
      },
      editToggle: {
        text: 'Allow Editing',
        hint: 'Toggle Directly Editing Cells',
        icon: 'edit',
        type: editingEnabled.value ? 'default' : null,
        stylingMode: editingEnabled.value ? 'contained' : null,
        onClick: () => editingEnabled.value = !editingEnabled.value
      }
    }
  })

  // Grid Data
  const initializeGrid = (e) => {
    searchGridInstance.value = e.component
    lastParams.value = null // need to clear params whenever the SearchGrid loads because the lastParams are stored in pinia
  }
  const editingEnabled = ref(false)

  // Column Config
  const saveState = (state) => {
    saveUserColumnConfig(state)
  }

  const loadState = async () => {
    const column_config = await getUserColumnConfig(cabinetId.value)

    const state = searchGridInstance.value.state()
    state.columns = column_config
    return state
  }

  // Compute textSearchStrings from searchBarObjects to pass into main doc cell so it can perform its own searches
  const textSearchStrings = computed(() => {
    return searchBarObjects.value.filter(obj => obj.type === 'text')
                        .map(obj => obj.text)
  })

  // Handle thumbnail click event
  const viewSingleDocument = (docId) => {
    // this route is handled by the backend which generates a basket and redirects the browser to that new basket
    window.open(`/document/${docId}`, '_blank')
  }

  const onEditorPreparing = (e) => {
    if (e.parentType === 'filterRow') {
      e.editorOptions.valueChangeEvent = 'change'
    }
  }

  const handleRowUpdated = () => {
    refreshSearchResults()
  }

  // Refresh grid when any docQueryParams change (selectedOnly, headerFilter, searchBarObjects)
  watch(docQueryParams, () => {
    refreshData()
  }, { deep: true })
</script>

<style lang="scss">
  #search-grid {
    // formatting the toolbar
    .dx-datagrid-header-panel {
      padding: 0 1rem;
    }

    .dx-datagrid-rowsview {
      // hovering over a row changes row color
      tr:hover {
        background-color: $gray-normal;
      }

      // visual difference when editing mode is turned on
      .editable-cell {
        background-image: linear-gradient(to top left, $gray-normal, transparent, transparent);
      }
    }

    .total-count-container {
      display: flex;
      align-items: baseline;
      gap: 0.4rem;
      font-weight: 500;

      .total-count {
        font-size: 1.2rem;
      }
    }

    // Column chooser: readable list area (DevExtreme default popup is very small).
    .dx-datagrid-column-chooser.dx-overlay-wrapper {
      z-index: 1200;
    }

    .dx-datagrid-column-chooser .dx-treeview-item {
      padding: 0.35rem 0.5rem;
    }
  }
</style>
